import get from 'lodash/get';
import getSplashApiClient from 'lib/SplashApiClient/singleton';
import { camelizeKeys } from 'humps';

/**
 * Wraps a SplashApiClient with the form builder API actions.
 */
export default class FormLibraryApiClient {
    /**
     * @param {SplashApiClient} apiClient
     */
    constructor(apiClient) {
        this.apiClient = apiClient;
    }

    /**
     * @param {string} formId
     * @param {string} name
     * @return {Promise}
     */
    addFormToLibrary(formId, name) {
        return this.apiClient.post('/form-library/form', {
            form_id: formId,
            name,
        });
    }

    /**
     * @param {number} page
     * * @param {string} searchTerm
     * @return {Promise}
     */
    getFormLibraryList(page = 0, searchTerm = '', formLibraryFormId = '', limit = 20) {
        const params = { page, limit };

        if (formLibraryFormId) {
            params.formLibraryFormId = formLibraryFormId;
        } else if (searchTerm) {
            params.name = searchTerm;
        }

        return this.apiClient
            .get('/form-library/forms', params)
            .then((response) => get(response, 'data.form_library_forms'));
    }

    /**
     * @param {number} formId
     * @param {string} eventId
     * @return {Promise}
     */
    applyFormToEvent(formId, eventId) {
        return this.apiClient.put('/form-library/apply', {
            formLibraryFormId: formId,
            eventId,
        });
    }

    /**
     * Apply a form template to an event using form ID
     * @param {string} eventId
     * @param {number} formLibraryFormId - The template form library ID to apply
     * @return {Promise}
     */
    applyFormWithFormIdToEvent(eventId, formId, formLibraryId) {
        return this.apiClient.post(`/events/${eventId}/forms/${formId}/apply`, {
            formLibraryId,
        });
    }

    /**
     * @param {number|string} formLibraryFormId
     * @return {Promise}
     * @memberof FormLibraryApiClient
     */
    getFormThemesAndEventsList(formLibraryFormId) {
        return this.apiClient.get(`/form-library/form/${formLibraryFormId}`);
    }

    /**
     * @param {string} name
     * @return {Promise}
     */
    createDefaultFormLibraryForm(name) {
        return this.apiClient.post('/form-library/create', { name });
    }

    /**
     * @param {number|string} formLibraryFormId
     * @return {Promise}
     */
    getFormLibraryFormData(formLibraryFormId) {
        return this.apiClient.get(`/form-library/${formLibraryFormId}/form`);
    }

    /**
     * @param {number|string} formLibraryFormId
     * @param {object} formData
     * @return {Promise}
     */
    saveFormLibraryFormData(formLibraryFormId, formData) {
        return this.apiClient.put(`/form-library/${formLibraryFormId}/settings`, formData);
    }

    /**
     * @param {number|string} formLibraryFormId
     * @return {Promise}
     */
    getFormLibraryFormSettingsData(formLibraryFormId) {
        return this.apiClient
            .get(`/form-library/${formLibraryFormId}/settings`)
            .then((response) => camelizeKeys(response));
    }

    /**
     * @param {number|string} formLibraryFormId
     * @return {Promise}
     */
    deleteFormLibraryForm(formLibraryFormId) {
        return this.apiClient.delete(`/form-library/${formLibraryFormId}`).then((response) => camelizeKeys(response));
    }
}

export const getFormLibraryApiClient = () => new FormLibraryApiClient(getSplashApiClient());
